import { makeArticle, notWorkingRelated } from "./learnFactory";

export const pageNewTreatmentsForTrd = makeArticle({
  slug: "new-treatments-for-treatment-resistant-depression",
  title: "New Treatments for Treatment-Resistant Depression | Neuro Wellness",
  description:
    "New treatments for treatment-resistant depression: SAINT® accelerated TMS, SPRAVATO®, and recent advances in brain stimulation for depression that hasn't responded to medication.",
  canonical: "/learn/new-treatments-for-treatment-resistant-depression",
  h1: "New Treatments for Treatment-Resistant Depression",
  breadcrumbLabel: "New Treatments for TRD",
  shortAnswer:
    "Recent treatments for treatment-resistant depression include SAINT® accelerated TMS (FDA-cleared September 2022), SPRAVATO® (esketamine, FDA-approved as monotherapy January 2025), and advances in theta burst stimulation. These treatments work through different mechanisms than standard antidepressants and offer new options for people whose depression hasn't responded to medication. 'Newer' does not mean 'better' for every person — each has specific indications, risks, and evidence limits. A psychiatric evaluation can determine which is appropriate for you.",
  keyTakeaways: [
    "SAINT® was FDA-cleared in September 2022.",
    "SPRAVATO® was approved as monotherapy for TRD in January 2025.",
    "These work through different mechanisms than standard antidepressants.",
    "'Newer' does not mean 'better' for every person.",
    "A psychiatric evaluation determines which is appropriate.",
  ],
  sections: [
    {
      heading: "SAINT® Accelerated TMS",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) was FDA-cleared in September 2022 (510(k) K220177) for adults with major depressive disorder who haven't improved on antidepressant medication. It delivers 50 sessions in five days with fMRI-guided targeting. In a sham-controlled trial, 79% of the active group reached remission vs 13% of sham. SAINT® is the newest FDA-cleared TMS protocol and the fastest depression treatment protocol available.",
      ],
    },
    {
      heading: "SPRAVATO® (Esketamine)",
      paragraphs: [
        "SPRAVATO® was first FDA-approved in 2019 for treatment-resistant depression (with an oral antidepressant). In January 2025, it was approved as monotherapy (without an oral antidepressant) for treatment-resistant depression. SPRAVATO® works through NMDA receptor modulation — a different mechanism than standard antidepressants. It is administered as a nasal spray in-office under monitoring.",
      ],
    },
    {
      heading: "Theta Burst Stimulation",
      paragraphs: [
        "Intermittent theta burst stimulation (iTBS) was FDA-cleared in 2018 as a once-daily TMS protocol. The THREE-D trial (2018) showed iTBS is non-inferior to standard 10 Hz TMS, with much shorter sessions (3 minutes vs 37 minutes). iTBS is the pulse pattern used in SAINT®. Theta burst represents an advance in TMS delivery, making treatment faster and more efficient.",
      ],
    },
    {
      heading: "'Newer' Does Not Mean 'Better'",
      paragraphs: [
        "Newer treatments offer additional options, but they do not automatically mean better outcomes for every person. Each treatment has specific indications, risks, evidence limits, and cost considerations. SAINT® has strong early evidence but small studies with short follow-up. SPRAVATO® causes dissociation and requires in-office monitoring. The appropriate treatment depends on your individual circumstances and should be discussed with a psychiatrist.",
      ],
    },
  ],
  faqs: [
    {
      question:
        "What are the newest treatments for treatment-resistant depression?",
      answer:
        "Recent treatments include SAINT® accelerated TMS (FDA-cleared 2022), SPRAVATO® as monotherapy (approved 2025), and theta burst stimulation. These work through different mechanisms than standard antidepressants. 'Newer' doesn't mean 'better' for every person — each has specific indications and risks.",
    },
    {
      question: "Is SAINT TMS a new treatment?",
      answer:
        "SAINT® was FDA-cleared in September 2022, making it the newest FDA-cleared TMS protocol. It delivers 50 sessions in five days with fMRI-guided targeting. In a sham-controlled trial, 79% of the active group reached remission. It is the fastest FDA-cleared depression treatment protocol.",
    },
    {
      question: "Is SPRAVATO a new treatment?",
      answer:
        "SPRAVATO® was first approved in 2019 (with an oral antidepressant) and as monotherapy in January 2025. It works through NMDA receptor modulation — a different mechanism than standard antidepressants. It is administered as a nasal spray in-office under monitoring.",
    },
    {
      question: "Are newer depression treatments better?",
      answer:
        "Not necessarily. Newer treatments offer additional options but don't automatically mean better outcomes. Each has specific indications, risks, evidence limits, and cost considerations. The appropriate treatment depends on your individual circumstances and should be discussed with a psychiatrist.",
    },
    {
      question:
        "What is the fastest treatment for treatment-resistant depression?",
      answer:
        "SAINT® is the fastest FDA-cleared depression treatment protocol — 50 sessions in five days. In the open-label study, average time to remission was 2.6 days. However, individual results vary, and the studies were small with short follow-up. SAINT® may be appropriate for treatment-resistant depression when a faster timeline is desired.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. FDA. 510(k) K220177 — Magnus Neuromodulation System with SAINT Technology.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: notWorkingRelated,
});
